import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import OnboardingWidget from '../components/OnboardingWidget';
import api, { getApiBaseUrl } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Building2, Users, CreditCard, AlertCircle, PlusCircle, 
  MapPin, ArrowRight, CheckCircle, Clock, Bed, Bath 
} from 'lucide-react';

const API_URL = getApiBaseUrl();

export default function LandlordDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalProperties: 0,
    activeListings: 0,
    pendingApplications: 0,
    monthlyRevenue: 0
  });
  const [properties, setProperties] = useState([]);
  const [listings, setListings] = useState([]);
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [isVerified, setIsVerified] = useState(false);
  const [loading, setLoading] = useState(true);

  const checkOnboardingStatus = useCallback(async () => {
    const token = localStorage.getItem('access_token') || localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/onboarding/landlord/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setOnboardingStatus(data);
        const verified = data.kyc_status === 'passed' && data.aml_status === 'passed';
        setIsVerified(verified);
      }
    } catch (error) {
      console.error('Failed to check onboarding:', error);
    }
  }, []);

  const loadDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch dashboard stats from dedicated endpoint
      let statsData = { totalProperties: 0, activeTenancies: 0, activeListings: 0, pendingApplications: 0, monthlyRevenue: 0 };
      try {
        const statsRes = await api.get('/properties/dashboard/stats');
        if (statsRes.data) {
          statsData = {
            totalProperties: statsRes.data.total_properties || 0,
            activeTenancies: statsRes.data.active_tenancies || statsRes.data.let_properties || 0,
            activeListings: statsRes.data.active_listings || 0,
            pendingApplications: statsRes.data.pending_applications || 0,
            monthlyRevenue: statsRes.data.monthly_revenue || 0
          };
        }
      } catch (e) {
        console.log('Stats endpoint not available, falling back');
      }
      
      // Fetch properties and listings for the list
      const [propertiesRes, listingsRes] = await Promise.all([
        api.get('/properties'),
        api.get('/listings').catch(() => ({ data: [] }))
      ]);

      // Handle both array and object responses
      const propertiesData = Array.isArray(propertiesRes.data) 
        ? propertiesRes.data 
        : (propertiesRes.data?.properties || []);
      const listingsData = Array.isArray(listingsRes.data) 
        ? listingsRes.data 
        : (listingsRes.data?.listings || []);
      
      setProperties(propertiesData);
      setListings(listingsData);
      
      // Use stats from API or calculate fallback
      if (statsData.totalProperties === 0 && propertiesData.length > 0) {
        const activeListings = listingsData.filter(l => l.status === 'active');
        statsData = {
          totalProperties: propertiesData.length,
          activeListings: activeListings.length,
          pendingApplications: 0,
          monthlyRevenue: activeListings.reduce((sum, l) => sum + (l.rent_amount || 0), 0)
        };
      }
      
      setStats(statsData);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboardData();
    checkOnboardingStatus();
  }, [loadDashboardData, checkOnboardingStatus]);

  const handleAddProperty = () => {
    navigate('/landlord/properties/new');
  };

  const getPropertyListing = (propertyId) => {
    return listings.find(l => l.property_id === propertyId && l.status === 'active');
  };

  return (
    <DashboardLayout>
      {/* Mobile-first container with proper padding */}
      <div className="pb-20 lg:pb-6">
        {/* Onboarding Widget */}
        <OnboardingWidget userRole="landlord" />

        {/* Header - mobile responsive */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-display font-bold text-slate-900">Dashboard</h1>
            <p className="text-sm sm:text-base text-slate-600">Manage your property portfolio</p>
          </div>
          <Button onClick={handleAddProperty} className="w-full sm:w-auto">
            <PlusCircle className="w-4 h-4 mr-2" />
            Add Property
          </Button>
        </div>

        {/* Stats Grid - 2x2 on mobile, 4 cols on desktop */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
          <StatCard
            label="Properties"
            value={stats.totalProperties}
            icon={Building2}
            loading={loading}
          />
          <StatCard
            label="Tenancies"
            value={stats.activeTenancies}
            icon={CheckCircle}
            color="green"
            loading={loading}
          />
          <StatCard
            label="Listings"
            value={stats.activeListings}
            icon={Users}
            color="amber"
            loading={loading}
          />
          <StatCard
            label="Revenue"
            value={formatCurrency(stats.monthlyRevenue)}
            sublabel="/month"
            icon={CreditCard}
            color="primary"
            loading={loading}
          />
        </div>

        {/* Quick Actions - mobile only */}
        <div className="grid grid-cols-2 gap-3 mb-6 lg:hidden">
          <Button variant="outline" onClick={() => navigate('/landlord/properties')} className="h-auto py-3">
            <div className="text-center">
              <Building2 className="w-5 h-5 mx-auto mb-1" />
              <span className="text-xs">Properties</span>
            </div>
          </Button>
          <Button variant="outline" onClick={() => navigate('/landlord/applications')} className="h-auto py-3">
            <div className="text-center">
              <Users className="w-5 h-5 mx-auto mb-1" />
              <span className="text-xs">Applications</span>
            </div>
          </Button>
        </div>

        {/* Properties Section */}
        <Card className="p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg sm:text-xl font-bold">Your Properties</h2>
            <Link to="/landlord/properties" className="text-primary text-sm font-medium flex items-center gap-1">
              View All <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : properties.length === 0 ? (
            <div className="text-center py-8">
              <Building2 className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="text-slate-600 mb-4">No properties yet</p>
              <Button onClick={handleAddProperty} size="sm">
                Add Your First Property
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {properties.slice(0, 4).map((property) => {
                const activeListing = getPropertyListing(property.id);
                const status = property.listing_status || (activeListing ? 'active' : 'draft');
                
                return (
                  <div 
                    key={property.id} 
                    className="flex items-center gap-3 p-3 rounded-xl border hover:bg-slate-50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/landlord/properties/${property.id}`)}
                  >
                    {/* Status indicator */}
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                      status === 'active' || activeListing ? 'bg-green-500' :
                      status === 'draft' ? 'bg-slate-300' :
                      'bg-amber-500'
                    }`} />
                    
                    {/* Property info */}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">
                        {property.address?.line1 || 'Address not set'}
                      </p>
                      <p className="text-xs text-slate-500 truncate">
                        {property.address?.city}, {property.address?.postcode}
                      </p>
                    </div>
                    
                    {/* Rent / Status */}
                    <div className="text-right flex-shrink-0">
                      {activeListing ? (
                        <>
                          <p className="font-semibold text-sm text-primary">
                            {formatCurrency(activeListing.rent_amount)}
                          </p>
                          <p className="text-xs text-green-600">Live</p>
                        </>
                      ) : (
                        <span className="text-xs px-2 py-1 bg-slate-100 rounded-full capitalize">
                          {status}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        {/* Pending Actions */}
        {stats.pendingApplications > 0 && (
          <Card className="p-4 sm:p-6 mt-4 border-amber-200 bg-amber-50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-5 h-5 text-amber-600" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-sm">Pending Applications</p>
                <p className="text-xs text-slate-600">
                  You have {stats.pendingApplications} application{stats.pendingApplications !== 1 ? 's' : ''} to review
                </p>
              </div>
              <Button size="sm" variant="outline" onClick={() => navigate('/landlord/applications')}>
                Review
              </Button>
            </div>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}

// Stat Card Component - Mobile optimized
function StatCard({ label, value, sublabel, icon: Icon, color = 'slate', loading }) {
  const colorClasses = {
    slate: 'bg-slate-100 text-slate-600',
    green: 'bg-green-100 text-green-600',
    amber: 'bg-amber-100 text-amber-600',
    primary: 'bg-primary/10 text-primary',
  };

  return (
    <Card className="p-3 sm:p-4">
      <div className="flex items-center gap-2 mb-2">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${colorClasses[color]}`}>
          <Icon className="w-4 h-4" />
        </div>
        <span className="text-xs sm:text-sm text-slate-500">{label}</span>
      </div>
      {loading ? (
        <div className="h-7 w-16 bg-slate-200 animate-pulse rounded"></div>
      ) : (
        <p className="text-xl sm:text-2xl font-bold">
          {value}
          {sublabel && <span className="text-xs sm:text-sm font-normal text-slate-500">{sublabel}</span>}
        </p>
      )}
    </Card>
  );
}
